import type { Capture } from '@daily/shared';
import clsx from 'clsx';
import { format } from 'date-fns';

interface ArtCardProps {
  capture: Capture;
  index: number;
  onClick?: () => void;
}

const ROTATIONS = ['-3deg', '2deg', '-1deg', '3deg', '-2deg', '1deg', '2deg', '-2deg', '3deg', '-1deg', '1deg'];
const TAPE_COLOURS = ['rgba(232,163,58,0.6)', 'rgba(233,75,107,0.55)', 'rgba(27,94,98,0.5)', 'rgba(107,140,94,0.55)'];

export function ArtCard({ capture, index, onClick }: ArtCardProps) {
  const rotation = ROTATIONS[index % ROTATIONS.length] ?? '0deg';
  const tape = TAPE_COLOURS[index % TAPE_COLOURS.length] ?? TAPE_COLOURS[0]!;
  const wide = capture.captureType === 'DRAWING' && index % 7 === 6;
  const date = format(new Date(capture.uploadedAt), 'd MMM');
  const author = capture.author
    ? capture.author.charAt(0).toUpperCase() + capture.author.slice(1)
    : labelForType(capture.captureType);

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'relative bg-white pt-2.5 pb-8 px-2.5 rounded-[3px] flex flex-col transition-transform duration-300 hover:!rotate-0 hover:scale-[1.04] hover:z-10',
        wide && 'col-span-2',
      )}
      style={{
        transform: `rotate(${rotation})`,
        boxShadow:
          '0 8px 20px -6px rgba(26,20,16,0.18), 0 2px 4px rgba(26,20,16,0.08)',
      }}
    >
      <span
        aria-hidden
        className="absolute -top-2 left-1/2 w-10 h-3.5 -translate-x-1/2 -rotate-2 z-[3] border-l border-r border-dashed border-black/5"
        style={{ background: tape }}
      />
      <div
        className="rounded-[2px] overflow-hidden mb-2.5"
        style={{
          aspectRatio: wide ? '4/3' : '3/4',
          backgroundColor: '#FFFDF5',
          backgroundImage: capture.thumbnailUrl ? `url(${capture.thumbnailUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="text-center font-script text-[16px] text-tomatoDeep leading-tight">
        {capture.caption ?? labelForType(capture.captureType)}
      </div>
      <div className="text-center font-mono text-[9px] text-inkFaded tracking-[0.08em] uppercase mt-1">
        {author} · {date}
      </div>
    </button>
  );
}

function labelForType(t: Capture['captureType']): string {
  switch (t) {
    case 'DRAWING':
      return 'Drawing';
    case 'SIENA_WRITING':
      return "Siena's writing";
    case 'LETTER':
      return 'Letter';
    case 'INVITE':
      return 'Invite';
    case 'CARD':
      return 'Card';
    case 'CERTIFICATE':
      return 'Certificate';
    case 'FLYER':
      return 'Flyer';
    default:
      return 'Other';
  }
}
